import React, { useState } from 'react'
import styled from '@emotion/styled'
import { useAuth } from '../context/AuthContext'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSignIn } from '@fortawesome/free-solid-svg-icons'
import { useNavigate } from 'react-router-dom'
import { Container, Form, Input, InputGroup, Message, Panel } from 'rsuite'
import EyeCloseIcon from '@rsuite/icons/EyeClose'
import VisibleIcon from '@rsuite/icons/Visible'
import { StandardButtonBrand } from '../components/ui_reusable/styles/Button.styles'
import theme from '../styles/theme'

const LoginHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  padding: 0.15rem 0;
`

const LoginTitle = styled.h1`
  font-family: Neuton, serif;
  font-weight: 600;
  font-size: 2rem;
  line-height: 1.15;
  letter-spacing: 0.02em;
  margin: 0;
  color: ${theme.colors.standardText};
`

const LoginSubtitle = styled.p`
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 0.72rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  margin: 0;
  color: ${theme.colors.fancyText};
`

const LoginSubmitButton = styled(StandardButtonBrand)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  margin-top: 0.25rem;
  padding: 0.7rem 1rem;

  svg {
    width: 0.85rem;
    height: 0.85rem;
  }
`

interface LoginFormData {
  email: string
  password: string
}

const Login: React.FC = () => {
  const { login, error, isLoading } = useAuth()
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const navigate = useNavigate()

  const handleSubmit = async (): Promise<void> => {
    const isLoggedIn = await login(formData)

    if (isLoggedIn) {
      navigate('/dashboard', { replace: true })
    }
  }

  return (
    <Container>
      <Panel
        bordered
        className="panel-signup"
        header={
          <LoginHeader>
            <LoginTitle>Login</LoginTitle>
            <LoginSubtitle>Citrus Predict</LoginSubtitle>
          </LoginHeader>
        }
      >
        {error && <Message type="error">{error}</Message>}
        <Form fluid onSubmit={handleSubmit}>
          <Form.Group controlId="email">
            <Form.ControlLabel>Email</Form.ControlLabel>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={(value: string) =>
                setFormData({ ...formData, email: value })
              }
              autoComplete="username"
              required
            />
          </Form.Group>
          <Form.Group controlId="password">
            <Form.ControlLabel>Password</Form.ControlLabel>
            <InputGroup inside>
              <Input
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(value: string) =>
                  setFormData({ ...formData, password: value })
                }
                autoComplete="current-password"
                required
              />
              <InputGroup.Button
                type="button"
                onClick={() => setShowPassword((visible) => !visible)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <VisibleIcon /> : <EyeCloseIcon />}
              </InputGroup.Button>
            </InputGroup>
          </Form.Group>
          <LoginSubmitButton type="submit" disabled={isLoading}>
            <FontAwesomeIcon icon={faSignIn} />
            {isLoading ? 'Logging in...' : 'Login'}
          </LoginSubmitButton>
        </Form>
      </Panel>
    </Container>
  )
}

export default Login
